import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth, can } from "@/auth";
import {
  UploadSimple, DownloadSimple, FileXls, X, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";

/**
 * Reusable Import / Export toolbar for an entity.
 *
 * Props:
 *   entity:        "contacts" | "installed_base" | "account_plans" | "opportunities"
 *   country:       active country code (used in export query string)
 *   canImport:     boolean (role-derived). If true, shows import + template buttons.
 *   exportOnly:    boolean. If true, only the export button is shown.
 *   previewCols:   list of { key, label } shown in the dry-run preview table.
 *   onImported:    callback after a successful real import.
 *   minRoleImport: optional minimum role required to see the import button (default "sales").
 */
export default function ImportExportBar({
  entity,
  country,
  canImport,
  exportOnly = false,
  previewCols = [
    { key: "operator", label: "Operator" },
    { key: "city", label: "City" },
  ],
  onImported,
  className = "",
}) {
  const { lang } = useI18n();
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const L = (fr, en) => (lang === "fr" ? fr : en);

  const download = (path) => {
    const a = document.createElement("a");
    a.href = `${API}${path}`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    runDryRun(f);
  };

  const runDryRun = async (f) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/${entity}/import?country=${country}&dry_run=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview({ summary: r.data, file: f });
    } catch (e) {
      toast.error(e.response?.data?.detail || L("Échec de la lecture du fichier", "Failed to read file"));
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async () => {
    if (!preview?.file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", preview.file);
      const r = await api.post(`/${entity}/import?country=${country}&dry_run=false`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        L(
          `${r.data.to_create} créé(s) · ${r.data.to_update} mis à jour`,
          `${r.data.to_create} created · ${r.data.to_update} updated`,
        ),
      );
      setPreview(null);
      onImported?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || L("Échec de l'import", "Import failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <button
        onClick={() => download(`/${entity}/export?country=${country}`)}
        data-testid={`${entity}-export-btn`}
        className="btn-ghost flex items-center gap-1.5"
        title={L("Exporter en Excel", "Export to Excel")}
      >
        <DownloadSimple size={13} weight="bold" />
        <span className="hidden sm:inline">{L("Exporter", "Export")}</span>
      </button>

      {!exportOnly && canImport && (
        <>
          <button
            onClick={() => download(`/${entity}/template`)}
            data-testid={`${entity}-template-btn`}
            className="btn-ghost flex items-center gap-1.5"
            title={L("Télécharger un modèle vide", "Download a blank template")}
          >
            <FileXls size={13} weight="bold" />
            <span className="hidden sm:inline">{L("Modèle", "Template")}</span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm,.csv"
            className="hidden"
            onChange={onPick}
            data-testid={`${entity}-import-input`}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid={`${entity}-import-btn`}
            className="btn-ghost flex items-center gap-1.5"
            title={L("Importer depuis Excel/CSV", "Import from Excel/CSV")}
          >
            <UploadSimple size={13} weight="bold" />
            <span className="hidden sm:inline">{L("Importer", "Import")}</span>
          </button>
        </>
      )}

      {preview && (
        <PreviewModal
          preview={preview}
          previewCols={previewCols}
          onCancel={() => setPreview(null)}
          onConfirm={onConfirm}
          busy={busy}
          L={L}
        />
      )}
    </div>
  );
}

function PreviewModal({ preview, previewCols, onCancel, onConfirm, busy, L }) {
  const { summary, file } = preview;
  const total = summary.to_create + summary.to_update;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      data-testid="import-preview-modal"
    >
      <div
        className="tile p-7 w-full max-w-2xl fade-in"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "var(--shadow-lg)" }}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-[20px] font-semibold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
              {L("Aperçu de l'import", "Import preview")}
            </h3>
            <p className="text-[12px] mt-1 font-mono" style={{ color: "var(--text-tertiary)" }}>
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded-full hover:bg-[var(--bg-subtle)]" data-testid="import-cancel-btn">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatCard label={L("À créer", "To create")} value={summary.to_create} color="#34A853" Icon={CheckCircle} />
          <StatCard label={L("À mettre à jour", "To update")} value={summary.to_update} color="#0D71DD" Icon={CheckCircle} />
          <StatCard
            label={L("Lignes ignorées", "Skipped")}
            value={summary.errors?.length || 0}
            color={summary.errors?.length ? "#F59E0B" : "var(--text-tertiary)"}
            Icon={WarningCircle}
          />
        </div>

        {summary.preview && summary.preview.length > 0 && (
          <div className="mb-4">
            <div className="label-tech mb-2">{L("Aperçu (10 premières lignes)", "Preview (first 10 rows)")}</div>
            <div className="overflow-x-auto rounded-[10px]" style={{ border: "1px solid var(--border)", maxHeight: 240 }}>
              <table className="tech w-full">
                <thead>
                  <tr>
                    <th>{L("Ligne", "Row")}</th>
                    {previewCols.map((c) => <th key={c.key}>{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {summary.preview.map((p, i) => (
                    <tr key={i}>
                      <td className="font-mono text-[11px]">{p.row}</td>
                      {previewCols.map((c) => (
                        <td key={c.key} className="text-[12px]">{p[c.key] ?? "—"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {summary.errors && summary.errors.length > 0 && (
          <div className="mb-4 p-3 rounded-[10px]" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid #F59E0B" }}>
            <div className="text-[12px] font-medium mb-1.5" style={{ color: "#F59E0B" }}>
              {L("Lignes ignorées", "Skipped rows")} ({summary.errors.length})
            </div>
            <ul className="text-[11px] font-mono space-y-0.5 max-h-24 overflow-y-auto" style={{ color: "var(--text-tertiary)" }}>
              {summary.errors.slice(0, 10).map((e, i) => (
                <li key={i}>• {L("ligne", "row")} {e.row} — {e.reason}</li>
              ))}
              {summary.errors.length > 10 && <li>… +{summary.errors.length - 10}</li>}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-2">
          <button onClick={onCancel} className="btn-ghost" data-testid="import-cancel-confirm-btn">
            {L("Annuler", "Cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || total === 0}
            className="btn-accent flex items-center gap-2"
            data-testid="import-confirm-btn"
          >
            <UploadSimple size={13} weight="bold" />
            {busy
              ? L("Import en cours…", "Importing…")
              : L(`Importer ${total} ligne(s)`, `Import ${total} row(s)`)}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, Icon }) {
  return (
    <div className="p-3 rounded-[10px]" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={12} weight="fill" style={{ color }} />
        <span className="label-tech">{label}</span>
      </div>
      <div className="text-[22px] font-semibold font-mono leading-none" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
